import React, { FunctionComponent, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Pressable } from 'react-native'

import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { getBreakpointValue } from '@lyrahealth-inc/shared-app-logic'

import { getAuroraAwareBackgroundColor, Subhead, SubheadSize } from '../..'
import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { Link } from '../../atoms/link/Link'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { AuroraAwareBackground } from '../../templates/auroraAwareBackground/AuroraAwareBackground'
import { ThemeType } from '../../utils'
import { mailTo, tID } from '../../utils/utils'

export interface GenericErrorProps {
  onReloadPress?: () => void
  error?: Error
  onView?: (error: Error) => void
}

const Container = styled.View<{ theme: ThemeType }>(({ theme: { breakpoints, colors, isAuroraTheme, spacing } }) => ({
  justifyContent: 'center',
  flexGrow: 1,
  alignItems: 'center',
  backgroundColor: getAuroraAwareBackgroundColor(isAuroraTheme, colors.backgroundSecondary),
  padding: getBreakpointValue(breakpoints, {
    default: spacing['0px'],
    isMobileSized: spacing['16px'],
  }),
}))

const StyledBodyText = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['8px'],
  marginBottom: theme.spacing['32px'],
  maxWidth: '464px',
}))

const ReloadButton = styled(PrimaryButton)({
  alignSelf: 'center',
})

const ErrorContainer = styled.View(() => ({
  marginTop: 32,
}))

export const GenericError: FunctionComponent<GenericErrorProps> = ({ onReloadPress = noop, error, onView = noop }) => {
  const [showError, setShowError] = useState(false)
  const {
    colors,
    breakpoints: { isMobileSized },
  } = useTheme() as ThemeType

  useEffect(() => {
    onView(error)
  }, [error, onView])

  const onCareLinkPress = () => mailTo('care@lyrahealth.com')
  return (
    <AuroraAwareBackground>
      <Container testID={tID('GenericError')}>
        <Pressable onLongPress={() => setShowError(!showError)} delayLongPress={3000}>
          <Subhead
            text={
              <FormattedMessage
                defaultMessage='Oops!'
                description='Title of the screen shown when there is an error indicating something went wrong.'
              />
            }
            size={isMobileSized ? SubheadSize.LARGE : SubheadSize.XLARGE}
            textAlign='center'
          />
        </Pressable>
        <StyledBodyText
          textAlign='center'
          color={colors.textSecondary}
          text={
            <FormattedMessage
              defaultMessage={`Something went wrong. Try reloading the page, and if there are still errors, contact <link>care@lyrahealth.com</link>`}
              description='A message shown to users when the error page is shown to direct the users to send an email if the problem persists.'
              values={{
                link: (chunks) => (
                  <Link text={chunks} color={colors.textSecondary} onPress={onCareLinkPress} underline medium />
                ),
              }}
            />
          }
        />
        <ReloadButton
          text={
            <FormattedMessage
              defaultMessage='Reload'
              description='Text for button to tell the user to reload the page.'
            />
          }
          onPress={onReloadPress}
        />
        {showError && !!error && (
          <ErrorContainer>
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            <BodyText text={`${error.name}: ${error.message}`} size={BodyTextSize.SMALL} color={colors.textError} />
          </ErrorContainer>
        )}
      </Container>
    </AuroraAwareBackground>
  )
}
